import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { cn } from './cn';

type Variant = 'magic' | 'primary' | 'soft' | 'ghost' | 'outline' | 'danger';
type Size = 'sm' | 'md' | 'lg' | 'xl';

const VARIANTS: Record<Variant, string> = {
  // The signature action colour. Gradient + hard under-shadow = "pressable candy".
  magic:
    'text-white border-magic-deep/40 shadow-cut [background-image:var(--gradient-magic)] hover:brightness-[1.06] active:brightness-95',
  primary:
    'bg-primary text-white border-primary-deep/40 shadow-cut hover:bg-primary-deep active:bg-primary-deep',
  soft: 'bg-primary-soft text-primary-deep border-primary/20 shadow-press hover:bg-primary-soft/70',
  outline: 'bg-card text-ink border-line shadow-press hover:bg-paper-deep',
  ghost: 'bg-transparent text-ink-soft border-transparent shadow-none hover:bg-paper-deep',
  danger: 'bg-card text-secondary border-secondary/40 shadow-press hover:bg-secondary-soft',
};

const SIZES: Record<Size, string> = {
  sm: 'min-h-[40px] px-l text-[15px] gap-s rounded-pebble',
  md: 'min-h-touch px-xl text-[17px] gap-s rounded-pebble',
  // Child-facing default: 56px target with real breathing room (spec §4.1).
  lg: 'min-h-touch-lg px-2xl text-[19px] gap-m rounded-blob',
  xl: 'min-h-[64px] px-2xl text-[21px] gap-m rounded-blob',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Renders as a router link instead of a button. */
  to?: string;
  /** Renders as an external anchor. */
  href?: string;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  block?: boolean;
  loading?: boolean;
}

const BASE =
  'relative inline-flex select-none items-center justify-center border-2 font-display font-semibold ' +
  'transition-[transform,filter,background-color,box-shadow] duration-150 ease-out ' +
  'active:translate-y-[2px] active:shadow-press disabled:pointer-events-none disabled:opacity-50';

/**
 * The one control every surface uses. Icons are always paired with words
 * (spec §4.1) — `icon` decorates, it never replaces the label.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    to,
    href,
    icon,
    iconRight,
    block,
    loading,
    className,
    children,
    disabled,
    ...rest
  },
  ref,
) {
  const classes = cn(BASE, VARIANTS[variant], SIZES[size], block && 'w-full', className);

  const inner = (
    <>
      {icon ? (
        <span aria-hidden className="shrink-0">
          {icon}
        </span>
      ) : null}
      <span className={cn(loading && 'opacity-0')}>{children}</span>
      {iconRight ? (
        <span aria-hidden className="shrink-0">
          {iconRight}
        </span>
      ) : null}
      {loading ? (
        <span className="absolute inset-0 grid place-items-center">
          <Spinner />
        </span>
      ) : null}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes} aria-disabled={disabled || undefined}>
        {inner}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={classes} target="_blank" rel="noreferrer noopener">
        {inner}
      </a>
    );
  }
  return (
    <button ref={ref} className={classes} disabled={disabled || loading} {...rest}>
      {inner}
    </button>
  );
});

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('h-5 w-5 animate-spin', className)}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
